import React, { forwardRef, useEffect, useState } from 'react'
import { Dialog, Slide, Theme } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { motion } from 'framer-motion'
import { fireReactGAEvent } from '../../../../Questions/utils'
import FormlessJourneyV3InterstitialContent from './FormlessJourneyV3InterstitialContent'
import FormlessLandingPage from '../LandingPage/FormlessLandingPage'
import { LenderDetails } from '../../../ApplicationFormContext'
import { AppFormEventParams } from '../../../../../services/events-service'

const MotionDiv = motion.div

const getDialogStyles = (theme: Theme) => ({
  padding: 0,
  textAlign: 'center',
  backgroundColor: '#fff',
  opacity: 1,
  boxShadow: 0,
  zIndex: '99999999999',
  minHeight: '100vh',
  overflowX: 'hidden',
  maxWidth: 'none',
  '.MuiDialog-container': {
    display: 'block',
    height: 'auto',
    minHeight: '100%',
    overflowY: 'auto',
    padding: 0
  },
  '.MuiPaper-root': {
    margin: '0 auto',
    width: '100%',
    padding: '0',
    boxShadow: 0,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    maxWidth: 1920
  },
  '.MuiDialogContent-root': {
    width: '100%',
    margin: 'auto',
    overflow: 'hidden',
    padding: theme.spacing(8),
    boxSizing: 'border-box'
  }
})

const fireInterstitialEvent = (experiment: string, started: boolean) => {
  fireReactGAEvent({
    category: 'Application Form',
    action: started ? 'Interstitial Started' : 'Interstitial Ended',
    label: experiment
  })
}

const Transition = forwardRef((props: TransitionProps & { children: React.ReactElement<any, any> }, ref: React.Ref<unknown>) => (
  <Slide
    unmountOnExit
    direction="right"
    ref={ref}
    {...props}
  />
))

export type InterstitialProps = {
  source: string
  experimentName: string
  isPreapproved: boolean
  requiredDetailsV1?: boolean
  lenderDetails?: LenderDetails
  redirectUrl?: string
  eventsServiceUrl: string
  appFormEventParams?: AppFormEventParams
}

const FormlessJourneyV3InterstitialWrapper = ({ source, experimentName, lenderDetails, redirectUrl, eventsServiceUrl, appFormEventParams }: InterstitialProps): JSX.Element => {
  const [showLanding, setShowLanding] = useState(false)
  const isPreapproved = lenderDetails?.preapproved ?? false

  useEffect(() => {
    fireInterstitialEvent(experimentName, true)
  }, [])

  const handleInterstitialComplete = () => {
    fireInterstitialEvent(experimentName, false)
    setShowLanding(true)
  }

  const dialogOpen = true

  return (
    <Dialog
      open={dialogOpen}
      data-testid={`${source}-interstitial`}
      TransitionComponent={Transition}
      keepMounted={false}
      aria-describedby="Transferring you to Zuto, our car finance partner"
      sx={(theme) => getDialogStyles(theme)}
      slotProps={{
        backdrop: { style: { backgroundColor: 'rgba(255,255,255,0)' } }
      }}
      fullWidth
    >
      {showLanding ? (
        <MotionDiv
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <FormlessLandingPage
            lenderDetails={lenderDetails}
            experimentName={experimentName}
            source={source}
            redirectUrl={redirectUrl}
            eventsServiceUrl={eventsServiceUrl}
            appFormEventParams={appFormEventParams}
          />
        </MotionDiv>
      ) : (
        <FormlessJourneyV3InterstitialContent
          onComplete={handleInterstitialComplete}
            isPreapproved={isPreapproved}
            source={source}
        />
      )}
    </Dialog>
  )
}

export default FormlessJourneyV3InterstitialWrapper
